import { GesturePhase, GestureRecognizerBase, PointerInput, PointerTracker } from "@m3e/web/gestures";

import { TapGestureDetail } from "./TapGestureDetail";
import { DefaultTapGestureOptions, TapGestureOptions } from "./TapGestureOptions";

/** A {@link GestureRecognizer} used to detect and interpret tap gestures from incoming input streams. */
export class TapGestureRecognizer extends GestureRecognizerBase<TapGestureOptions, TapGestureDetail> {
  /** @private */ readonly #state = new Map<
    number,
    { tracker: PointerTracker; pressTimestamp: number; releaseTimestamp?: number; accepted?: boolean }
  >();

  /** @private */ #timeout?: number;
  /** @private */ #started = false;
  /** @private */ #cancelled = false;
  /** @private */ #cancelling = false;

  /** @inheritdoc */
  override get defaultOptions(): TapGestureOptions {
    return { ...DefaultTapGestureOptions };
  }

  /** @inheritdoc */
  override shouldCapturePointer(input: PointerInput): boolean {
    return this.#state.has(input.inputId);
  }

  /** @inheritdoc */
  override _onPointerDown(input: PointerInput): void {
    // Reject duplicate input or too many pointers.
    if (this.#state.has(input.inputId) || this.#state.size >= this.options.pointers) {
      this.#cancel();
      return;
    }

    // Reject if press exceeds interval from initial press.
    if (this.#state.size > 0 && this.options.maxPressInterval > 0) {
      const initialPress = Math.min(...[...this.#state.values()].map((x) => x.pressTimestamp));
      if (input.timestamp - initialPress > this.options.maxPressInterval) {
        this.#cancel();
        return;
      }
    }

    this.#state.set(input.inputId, { tracker: new PointerTracker(input), pressTimestamp: input.timestamp });

    // Ensure max duration is not exceeded (starts from first pointer down).
    if (this.options.maxDuration > 0 && this.#state.size === 1) {
      this.#timeout = setTimeout(() => this.#cancel(), this.options.maxDuration);
    }

    // Input is deferred in order to notify the recognizer via reject when other gesture accepts.
    this._defer(input.inputId);

    if (this.#state.size === this.options.pointers) {
      // Emit start only when all pointers are down.
      this.#started = true;
      this._emit(this.#createDetail("start"));
    }
  }

  /** @inheritdoc */
  override _onPointerMove(input: PointerInput): void {
    // Ignore if pointer is not tracked.
    const state = this.#state.get(input.inputId);
    if (!state) return;

    state.tracker.append(input);

    // Only enforce displacement after gesture has started and not cancelled.
    if (!this.#started || this.#cancelled) return;

    // Reject when the centroid of the pointers moves beyond the allowed displacement.
    const trackers = [...this.#state.values()].map((x) => x.tracker);
    if (PointerTracker.centroid(...trackers).totalDisplacement > this.options.maxDisplacement) {
      this.#cancel();
    }
  }

  /** @inheritdoc */
  override _onPointerUp(input: PointerInput): void {
    // Ignore if pointer is not tracked.
    const state = this.#state.get(input.inputId);
    if (!state) return;

    if (!this.#started) {
      this.#cancel();
      return;
    }

    // Reject if release exceeds interval from initial release.
    if (this.options.maxReleaseInterval > 0) {
      const states = [...this.#state.values()];
      if (states.some((x) => x.releaseTimestamp !== undefined)) {
        const initialRelease = Math.min(
          ...states.filter((x) => x.releaseTimestamp !== undefined).map((x) => x.releaseTimestamp!),
        );
        if (input.timestamp - initialRelease > this.options.maxReleaseInterval) {
          this.#cancel();
          return;
        }
      }
    }

    state.tracker.append(input);
    state.releaseTimestamp = input.timestamp;

    // Clear max duration timeout when all pointers have pressed and released.
    if (this.#started && [...this.#state.values()].every((x) => x.releaseTimestamp !== undefined)) {
      clearTimeout(this.#timeout);
      this.#timeout = undefined;
    }

    // Acceptance emits the final detail only after every pointer is accepted.
    this._accept(input.inputId);
  }

  /** @inheritdoc */
  override _onPointerCancel(input: PointerInput) {
    const state = this.#state.get(input.inputId);
    if (state) {
      this.#cancel();
    }
  }

  /** @inheritdoc */
  override _onAccept(inputId: number): void {
    const state = this.#state.get(inputId);
    if (!state) return;

    state.accepted = true;

    // Only emit end if gesture started, not cancelled and all inputs are accepted.
    if (this.#started && !this.#cancelled && [...this.#state.values()].every((x) => x.accepted)) {
      this._emit(this.#createDetail("end"));
      this.reset();
    }
  }

  /** @inheritdoc */
  protected override _onReject(inputId: number): void {
    if (this.#state.has(inputId)) {
      this.#cancel(inputId);
    }
  }

  /** @inheritdoc */
  reset(): void {
    clearTimeout(this.#timeout);
    this.#timeout = undefined;
    this.#state.clear();
    this.#started = false;
    this.#cancelled = false;
  }

  /** @private */
  #cancel(excludeInputId?: number): void {
    // Resolver callbacks are synchronous, so guard the rejection loop against recursive cancellation.
    if (this.#state.size === 0 || this.#cancelling) return;
    try {
      this.#cancelling = true;

      // Emit cancel if started and not yet cancelled.
      if (this.#started && !this.#cancelled) {
        this._emit(this.#createDetail("cancel"));
      }

      this.#cancelled = true;

      // Reject all inputs and reset.
      [...this.#state.keys()].filter((x) => x !== excludeInputId).forEach((x) => this._reject(x));
      this.reset();
    } finally {
      this.#cancelling = false;
    }
  }

  /** @private */
  #createDetail(phase: GesturePhase): TapGestureDetail {
    const trackers = [...this.#state.values()].map((x) => x.tracker);
    const centroid = PointerTracker.centroid(...trackers);
    return {
      gestureName: "tap",
      phase,
      inputId: [...this.#state.keys()],
      timestamp: Math.max(...trackers.map((x) => x.current.timestamp)),
      clientX: centroid.startClientX,
      clientY: centroid.startClientY,
      localX: centroid.startLocalX,
      localY: centroid.startLocalY,
      duration: trackers.reduce((sum, x) => sum + x.detail.duration, 0) / trackers.length,
    };
  }
}
